import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Wallet, ExternalLink, Copy, Leaf, Link2, FileCheck2, RefreshCw } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { getTransparency } from "@/lib/api";

const fmtUsd = (n) =>
  typeof n === "number" ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—";

const fmtDate = (s) => {
  try {
    return new Date(s + "T00:00:00").toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return s;
  }
};

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");

export default function Transparency() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getTransparency()
      .then(setData)
      .catch(() => toast.error("Could not load transparency data."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const wallet = data?.charity_wallet || "";
  const donations = data?.donations || [];
  const scanBase = wallet ? `https://basescan.org/address/${wallet}` : "#";

  const copyAddr = async () => {
    try {
      await navigator.clipboard.writeText(wallet);
      toast.success("Charity wallet address copied.");
    } catch {
      toast.error("Copy failed — select it manually.");
    }
  };

  return (
    <main className="bg-background text-foreground min-h-screen overflow-x-hidden" data-testid="transparency-page">
      <Navbar />

      {/* Header */}
      <section className="pt-36 pb-16 px-6 md:px-12 max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ember">
            <ShieldCheck className="w-4 h-4" /> Impact &amp; Transparency
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
            Prove it. Don't <span className="text-ember">promise</span> it.
          </h1>
          <p className="mt-6 text-base text-muted-foreground max-w-2xl leading-relaxed">
            Every dollar AfterBurn directs toward wildfire recovery is verifiable. Watch the charity
            wallet grow live on the Base blockchain, and see every donation logged below with an
            on-chain transaction and an official receipt from the receiving organization.
          </p>
        </motion.div>
      </section>

      {/* Live charity wallet balance */}
      <section className="px-6 md:px-12 max-w-6xl mx-auto pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative border border-white/10 bg-card/60 p-8 sm:p-10 overflow-hidden"
          data-testid="charity-wallet-card"
        >
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-ember/10 blur-3xl rounded-full pointer-events-none" />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
              <Wallet className="w-4 h-4 text-ember" /> Charity Wallet · Base Network
            </div>
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live on-chain
            </span>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <div className="text-4xl sm:text-6xl font-display font-bold text-ember font-mono tracking-tight" data-testid="charity-balance-eth">
                {loading ? "…" : data?.balance_eth != null ? `${data.balance_eth} ETH` : "— ETH"}
              </div>
              <div className="mt-2 text-sm text-muted-foreground" data-testid="charity-balance-usd">
                ≈ {fmtUsd(data?.balance_usd)} USD{" "}
                {data?.eth_price_usd ? (
                  <span className="text-muted-foreground/60">· ETH @ {fmtUsd(data.eth_price_usd)}</span>
                ) : null}
              </div>
              {data?.token_balance != null && (
                <div className="mt-3 inline-flex items-center gap-2 font-mono text-sm text-foreground/90 border border-white/10 bg-background px-3 py-1.5" data-testid="charity-token-balance">
                  <Leaf className="w-3.5 h-3.5 text-ember" />
                  {data.token_balance.toLocaleString()} {data.token_symbol}
                </div>
              )}
            </div>
            <button
              onClick={load}
              disabled={loading}
              data-testid="refresh-balance-btn"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase hover:border-ember hover:text-ember transition-colors disabled:opacity-60 self-start"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Wallet address</div>
            <div className="flex items-center gap-3 flex-wrap">
              <code className="font-mono text-sm text-foreground/90 break-all" data-testid="charity-wallet-address">
                <span className="hidden sm:inline">{wallet || "Not configured"}</span>
                <span className="sm:hidden">{shortAddr(wallet) || "Not configured"}</span>
              </code>
              {wallet && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyAddr}
                    data-testid="copy-wallet-btn"
                    className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-mono text-[11px] uppercase hover:border-ember hover:text-ember transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                  <a
                    href={scanBase}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="view-basescan-btn"
                    className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-mono text-[11px] uppercase hover:border-ember hover:text-ember transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> BaseScan
                  </a>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </section>

      {/* How we prove it */}
      <section className="px-6 md:px-12 max-w-6xl mx-auto pb-16">
        <h2 className="text-base md:text-lg font-display font-bold tracking-tight mb-6">
          How every donation is proven — two ways
        </h2>
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {[
            {
              icon: Link2,
              title: "On-chain (the money leaving)",
              body: "Funds leave the public charity wallet in a blockchain transaction anyone can verify on BaseScan — permanent, timestamped, and impossible to fake.",
            },
            {
              icon: FileCheck2,
              title: "Official receipt (the money arriving)",
              body: "The receiving organization issues a standard donation receipt confirming the funds landed. We publish it alongside the on-chain proof to close the loop end-to-end.",
            },
          ].map((c) => (
            <div key={c.title} className="bg-background p-8" data-testid="proof-card">
              <c.icon className="w-6 h-6 text-ember" />
              <h3 className="mt-4 font-display font-bold text-lg">{c.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Donations ledger */}
      <section className="px-6 md:px-12 max-w-6xl mx-auto pb-28">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-ember" />
            <h2 className="font-display font-bold text-xl">Donations Ledger</h2>
          </div>
          {donations.length > 0 && (
            <span className="font-mono text-xs text-muted-foreground" data-testid="donations-total">
              {donations.length} donation{donations.length === 1 ? "" : "s"} · {fmtUsd(data?.total_donated_usd)} total
            </span>
          )}
        </div>

        <div className="border border-white/10 bg-card/40 overflow-x-auto" data-testid="donations-table">
          <table className="w-full font-mono text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-white/10 text-xs uppercase tracking-wider">
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Organization</th>
                <th className="px-5 py-4 text-right">Amount</th>
                <th className="px-5 py-4 text-right">Proof</th>
              </tr>
            </thead>
            <tbody>
              {donations.map((d, i) => (
                <tr key={d.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`donation-row-${i}`}>
                  <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">{fmtDate(d.date)}</td>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-foreground">{d.organization}</div>
                    {d.note && <div className="mt-1 text-[11px] text-muted-foreground font-normal">{d.note}</div>}
                  </td>
                  <td className="px-5 py-4 text-right text-ember whitespace-nowrap">
                    {d.amount_usd != null && <div>{fmtUsd(d.amount_usd)}</div>}
                    {d.amount_eth != null && <div className="text-muted-foreground text-xs">{d.amount_eth} ETH</div>}
                    {d.amount_usd == null && d.amount_eth == null && <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      {d.tx_url && (
                        <a href={d.tx_url} target="_blank" rel="noreferrer" data-testid={`donation-tx-${i}`} className="inline-flex items-center gap-1 text-[11px] uppercase border border-white/15 px-2.5 py-1 hover:border-ember hover:text-ember transition-colors">
                          <Link2 className="w-3 h-3" /> On-chain
                        </a>
                      )}
                      {d.receipt_url && (
                        <a href={d.receipt_url} target="_blank" rel="noreferrer" data-testid={`donation-receipt-${i}`} className="inline-flex items-center gap-1 text-[11px] uppercase border border-white/15 px-2.5 py-1 hover:border-ember hover:text-ember transition-colors">
                          <FileCheck2 className="w-3 h-3" /> Receipt
                        </a>
                      )}
                      {!d.tx_url && !d.receipt_url && <span className="text-muted-foreground text-xs">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && donations.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-16 text-center">
                    <Leaf className="w-8 h-8 text-ember/40 mx-auto mb-4" />
                    <p className="text-muted-foreground max-w-md mx-auto leading-relaxed" data-testid="donations-empty">
                      No donations yet. Our first distributions will appear here after launch — each with
                      an on-chain BaseScan link and an official receipt. <span className="text-foreground">Prove it, don't promise it.</span>
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-muted-foreground/70 max-w-2xl leading-relaxed">
          AfterBurn is not officially partnered with any charity. Funds accumulate in the public wallet
          above and are donated to established, verified wildfire-recovery and reforestation
          organizations. Every distribution is published here. This is not financial advice.
        </p>
      </section>

      <Footer />
    </main>
  );
}
